// Note-creation orchestration (web equivalent of NoteCreateScreen's
// startNote): create the Firestore doc with status 'uploading', then upload
// the source file — the Storage finalize trigger drives the rest of the
// pipeline (queued → transcribing → summarizing → ready).
//
// Uploads continue in the background after navigation; progress and failures
// are published per-note so the note page can render them.
import type {Note, NoteFileType} from '../types/Note';
import {createNoteDoc} from './firestoreNotes';
import {uploadNoteFile, MAX_UPLOAD_BYTES} from './storageService';
import {createYoutubeNote} from './functionsService';
import {getVoiceLanguage, resolveSummaryLanguage} from './settings';

export interface UploadState {
  progress: number;
  error: string | null;
  uploading: boolean;
}

type Listener = (state: UploadState) => void;

const states = new Map<string, UploadState>();
const listeners = new Map<string, Set<Listener>>();

const publish = (noteId: string, state: UploadState) => {
  states.set(noteId, state);
  listeners.get(noteId)?.forEach(fn => fn(state));
};

export const subscribeUpload = (
  noteId: string,
  listener: Listener,
): (() => void) => {
  let set = listeners.get(noteId);
  if (!set) {
    set = new Set();
    listeners.set(noteId, set);
  }
  set.add(listener);
  const current = states.get(noteId);
  if (current) {
    listener(current);
  }
  return () => {
    set.delete(listener);
  };
};

export class FileTooLargeError extends Error {
  constructor() {
    super('File exceeds the 200MB upload limit');
  }
}

interface PendingUpload {
  uid: string;
  file: Blob;
  ext: string;
}

// Kept so a failed upload can be retried without re-picking the file.
const pendingUploads = new Map<string, PendingUpload>();

const runUpload = async (noteId: string): Promise<void> => {
  const pending = pendingUploads.get(noteId);
  if (!pending) {
    return;
  }
  publish(noteId, {progress: 0, error: null, uploading: true});
  try {
    await uploadNoteFile(pending.uid, noteId, pending.file, pending.ext, pct =>
      publish(noteId, {progress: pct, error: null, uploading: true}),
    );
    pendingUploads.delete(noteId);
    publish(noteId, {progress: 100, error: null, uploading: false});
  } catch (e) {
    console.warn('upload failed', e);
    publish(noteId, {
      progress: 0,
      error: e instanceof Error ? e.message : String(e),
      uploading: false,
    });
  }
};

export const retryUpload = (noteId: string): void => {
  void runUpload(noteId);
};

/**
 * Creates a note from a recorded blob or picked/dropped file and starts the
 * upload. Resolves with the note id as soon as the Firestore doc exists so
 * the caller can navigate to the note page immediately.
 */
export const createNoteFromFile = async (
  uid: string,
  file: Blob,
  ext: string,
  options?: {duration?: number; language?: string | null},
): Promise<string> => {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new FileTooLargeError();
  }
  const id = String(Date.now());
  const fileType: NoteFileType = ext === 'pdf' ? 'pdf' : 'audio';
  const note: Note = {
    id,
    createdAt: new Date().toString(),
    duration: options?.duration ?? 0,
    language: options?.language !== undefined ? options.language : getVoiceLanguage(),
    summaryLanguage: resolveSummaryLanguage(),
    status: 'uploading',
    fileType,
  };
  await createNoteDoc(uid, note);
  pendingUploads.set(id, {uid, file, ext});
  void runUpload(id);
  return id;
};

/** Creates a YouTube note via the callable; processing is fully server-side. */
export const createNoteFromYoutube = async (
  videoId: string,
): Promise<string> => {
  const result = await createYoutubeNote(
    videoId,
    getVoiceLanguage(),
    resolveSummaryLanguage(),
  );
  return result.noteId;
};

/** Extracts a YouTube video id from a pasted URL (watch/shorts/youtu.be). */
export const parseYoutubeVideoId = (input: string): string | null => {
  const trimmed = input.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/,
    /(?:youtu\.be\/)([\w-]{11})/,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/,
    /(?:youtube\.com\/embed\/)([\w-]{11})/,
    /(?:youtube\.com\/live\/)([\w-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      return match[1];
    }
  }
  return /^[\w-]{11}$/.test(trimmed) ? trimmed : null;
};
